import type { AppDefinition } from '../types';
import { AppHome } from './AppHome';
import { CardBack } from './CardBack';
import { CardFront } from './CardFront';
import { MissRow } from './MissRow';
import type { GrammatikPayload } from './types';

/**
 * Grammatik — one rule a day.
 *
 * The first app in the tree whose content is not a deck of words. A1 grammar is
 * a finite closed set of about fifty rules, so this is a curriculum with a
 * finish line rather than an endless feed: six weeks of one short lesson a day,
 * then the same deck as review forever. That single fact drives everything —
 * the ordering matters, the content is worth verifying exercise by exercise
 * because there are only ~400 of them, and the app needs an explicit answer for
 * what happens on day fifty-one.
 *
 * Each lesson explains its rule against BOTH Portuguese and English, and
 * carries an `anchor` naming which of them German actually behaves like here.
 * That is the part no monolingual app can do, and it is load-bearing: knowing
 * whether to import or suppress an intuition is most of the work of an A1 rule.
 * It is also the part most easily got wrong — a third of the first pass was
 * factually wrong about Portuguese — so the anchors carry provenance and the
 * seeder refuses production until a native speaker has read them.
 *
 * What this app does NOT own: attempts, streaks, the archive sweep, the
 * struggling flag, stats. One exercise is one `cards` row, so all of that comes
 * from the shared engine unchanged. It owns exactly two things — a deck that is
 * ordered rather than sampled, and a lesson screen that is read rather than
 * answered.
 */
export const grammatikApp: AppDefinition<GrammatikPayload> = {
  id: 'grammatik',
  display_name: 'Grammatik',
  CardFront,
  CardBack,
  MissRow,
  Home: AppHome,
};
